import React from "react";

import { segmentBisector, segmentCount } from "../../chartGeometry";
import { ConfigData } from "../../config";
import { Item, QuadrantConfig } from "../../model";
import RadarChart from "../Chart/RadarChart";
import Link from "../Link/Link";
import "./radar-grid.scss";

/**
 * Absolute corner placement of the four-segment radar. Frozen: this is the
 * layout that has been in production, and the Tech radar must not move.
 */
const LEGACY_FOUR_LABEL_STYLES: React.CSSProperties[] = [
  { top: 0, left: 0 },
  { top: 0, right: 0 },
  { bottom: 0, left: 0 },
  { bottom: 0, right: 0 },
];

const QuadrantLabel: React.FC<{
  quadrantConfig: QuadrantConfig;
  quadrantName: string;
  quadrantLabel: string;
  /** "Quadrant" only makes sense for four of them. */
  kindLabel: string;
  style?: React.CSSProperties;
}> = ({ quadrantConfig, quadrantName, quadrantLabel, kindLabel, style }) => {
  return (
    <div className="quadrant-label" style={style}>
      <div className="split">
        <div className="split__left">
          <small style={{ color: quadrantConfig.colour }}>
            {kindLabel} {quadrantConfig.position}
          </small>
        </div>
        <div className="split__right">
          <Link className="icon-link" pageName={`${quadrantName}`}>
            <span className="icon icon--pie icon-link__icon zoom-in-text" />
            Zoom In
          </Link>
        </div>
      </div>
      <hr style={{ borderColor: quadrantConfig.colour }} />
      <h4 className="headline">{quadrantLabel}</h4>
      <div className="description">{quadrantConfig.description}</div>
    </div>
  );
};

/**
 * Key to the blip shapes. Kept out of `.radar-grid` on purpose: that element is
 * the positioning context of the four corner labels, so anything added inside it
 * would push them off the chart.
 */
const Legend: React.FC = () => (
  <div className="radar-legend">
    <span className="radar-legend__entry">
      <span className="icon icon--blip_new radar-legend__icon" />
      New this quarter
    </span>
    <span className="radar-legend__entry">
      <span className="icon icon--blip_changed radar-legend__icon" />
      Moved this quarter
    </span>
    <span className="radar-legend__entry">
      <span className="icon icon--blip_default radar-legend__icon" />
      Unchanged
    </span>
  </div>
);

type Entry = [string, QuadrantConfig];

/** Segments whose bisector points right (or left), ordered top to bottom. */
const bySide = (entries: Entry[], n: number, right: boolean) =>
  entries
    .filter(
      ([, quadrant]) => segmentBisector(quadrant.position, n).x >= 0 === right
    )
    .sort(
      (a, b) =>
        segmentBisector(a[1].position, n).y -
        segmentBisector(b[1].position, n).y
    );

const RadarGrid: React.FC<{ items: Item[]; config: ConfigData }> = ({
  items,
  config,
}) => {
  const entries = Object.entries(config.quadrantsMap) as Entry[];
  const n = segmentCount(config);

  const label = ([name, quadrant]: Entry, style?: React.CSSProperties) => (
    <QuadrantLabel
      key={name}
      quadrantConfig={quadrant}
      quadrantName={name}
      quadrantLabel={config.quadrants[name]}
      kindLabel={n === 4 ? "Quadrant" : "Segment"}
      style={style}
    />
  );

  if (n === 4) {
    return (
      <>
        <div className="radar-grid">
          <RadarChart items={items} config={config} />
          {entries.map((entry) =>
            label(entry, LEGACY_FOUR_LABEL_STYLES[entry[1].position - 1])
          )}
        </div>
        <Legend />
      </>
    );
  }

  /*
    Beyond four segments the corners run out: the labels go into two columns
    flanking the chart, each column ordered the way the segments are ordered on
    the chart itself (top to bottom, following their bisector).
  */
  return (
    <>
      <div className="radar-grid radar-grid--flanked">
        <div className="radar-grid__side">
          {bySide(entries, n, false).map((entry) => label(entry))}
        </div>
        <div className="radar-grid__chart">
          <RadarChart items={items} config={config} />
        </div>
        <div className="radar-grid__side">
          {bySide(entries, n, true).map((entry) => label(entry))}
        </div>
      </div>
      <Legend />
    </>
  );
};

export default RadarGrid;
